import { Box } from '@zillow/constellation-10';
import { ChatAvatar, ChatBubbleWithLinkFeatureToggle } from '@zg-rentals/satellite-components';
import { useUserWarnings } from '../../../hooks/useUserWarnings';
import brandUtils from '../../../utils/brandUtils';
import MultibrandChatAvatar from '../../MultibrandChatAvatar';
import useGetFeatureFlagsAndSettings from '../../../hooks/useGetFeatureFlagsAndSettings';
import {
  emitExternalLinkClickedInExternalLinkWarningModalEvent,
  emitOpenExternalLinkWarningModalEvent,
} from '../../../constants/clickstreamEvents/externalLinkWarningModal';

type Props = {
  brand: Brand;
  message: string;
};

/*
 * We disable sending messages to the landlord and display a
 * ChatDisabledMessage when acceptsReplies is false.
 *
 * acceptsReplies will be false if a conversation is:
 *   For a Multifamily Listing OR a conversation is marked as API_ONLY_LEAD
 *   AND landlord has not yet replied in the conversation.
 * Any other conversation will have acceptsReplies as true
 */
export default function ChatDisabledMessage({ brand, message }: Props) {
  const { features } = useGetFeatureFlagsAndSettings();
  const { enableMessageLink, enableExternalLinkWarningModal } = features;
  const isHotPadsBrand = brandUtils.isHotpads(brand);
  const isZillowBrand = brandUtils.isZillow(brand);

  const { externalLinkWarningData } = useUserWarnings({
    enableExternalLinkWarning: !!enableExternalLinkWarningModal,
  });

  return (
    <Box css={{ display: 'flex' }}>
      {isZillowBrand ? <ChatAvatar type="zillow" hasTimestampInMessage={false} /> : <MultibrandChatAvatar />}

      <ChatBubbleWithLinkFeatureToggle
        variant="default"
        enableClickableLinks={!!enableMessageLink}
        enableExternalLinkWarningModal={!!enableExternalLinkWarningModal}
        externalLinkWarningData={externalLinkWarningData}
        brand={isHotPadsBrand ? 'hotpads' : 'zillow'}
        onImpact={false}
        shouldIncludeGutter
        onExternalLinkWarningModalOpen={emitOpenExternalLinkWarningModalEvent}
        onExternalLinkClickInModal={emitExternalLinkClickedInExternalLinkWarningModalEvent}
      >
        {message}
      </ChatBubbleWithLinkFeatureToggle>
    </Box>
  );
}
